import { useCallback } from "react";
import { useDateFnsFormattersWithLocale } from "@tellescope/ui";
import { TellescopeModelFromName } from "@tellescope/tanstack-query";
import { FormResponseValueAnswer } from "@tellescope/types-models";
import { parseTimeAnswer } from "../../utils";

/**
 * Returns a resolver that reads a field's configured `options.default` into a value of the right
 * shape for its type, returning undefined when there is no usable default.
 *
 * `date` and `dateString` fields fall back to `options.prefillCurrentDate`, seeding midnight today
 * rather than the current instant, matching how `minDateOffsetMs`/`maxDateOffsetMs` are anchored
 * (see {@link dateFromOffsetMs}).
 *
 * `Time` is seeded with the current local time whether or not `prefillCurrentDate` is set, matching
 * legacy (`hooks.tsx:740-741`): a `Time` field is never empty on arrival there, and forms authored
 * against that behavior treat a required `Time` field as already satisfied.
 */
export const useResolveSeededDefault = () => {
  const { format } = useDateFnsFormattersWithLocale();

  return useCallback(
    (field: TellescopeModelFromName<"form_fields">): FormResponseValueAnswer["value"] => {
      const configuredDefault = field.options?.default;
      const stringDefault =
        typeof configuredDefault === "string" && configuredDefault !== "" ? configuredDefault : undefined;

      switch (field.type) {
        case "string":
        case "stringLong":
        case "email":
        case "phone":
        case "Timezone":
          return typeof configuredDefault === "string" ? configuredDefault : undefined;
        case "number": {
          if (typeof configuredDefault === "number") return configuredDefault;
          if (stringDefault === undefined) return undefined;
          const parsed = Number(stringDefault);
          return Number.isNaN(parsed) ? undefined : parsed;
        }
        case "date": {
          if (stringDefault === undefined) {
            if (!field.options?.prefillCurrentDate) return undefined;
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            return today;
          }
          const parsed = new Date(stringDefault);
          return Number.isNaN(parsed.getTime()) ? undefined : parsed;
        }
        case "dateString":
          if (stringDefault !== undefined) return stringDefault;
          return field.options?.prefillCurrentDate ? format(new Date(), "MM-dd-yyyy") : undefined;
        case "Time":
          return (stringDefault && parseTimeAnswer(stringDefault)?.local) || format(new Date(), "HH:mm");
        case "multiple_choice":
        case "Dropdown":
          if (Array.isArray(configuredDefault)) return configuredDefault;
          return stringDefault === undefined ? undefined : [stringDefault];
        default:
          return undefined;
      }
    },
    [format]
  );
};
